import { yupResolver } from "@hookform/resolvers/yup";
import {
  dependenciesFetcher,
  requestFetcher,
  requestService,
  useRequestDependencies,
  useRequestPersistedStore,
  useRequestStore,
} from "entities/Request";
import { useState } from "react";
import { Modal, Form as BootstrapForm, Badge } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Button, Form } from "shared/ui";
import useSWRMutation from "swr/mutation";
import * as yup from "yup";
import FilesWithErrors from "../FilesWithErrors/FilesWithErrors";
import { baseApiService } from "shared/api";

function UploadToOCRModal({ show, handleClose }) {
  const { t } = useTranslation(["OCR", "Shared"]);
  const request = useRequestStore((state) => state.request);
  const viewRequestAs = useRequestPersistedStore(
    (state) => state.viewRequestAs
  );
  const [filesWithErrors, setFilesWithErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdatingRequest, setIsUpdatingRequest] = useState(false);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        ocr_attachments: yup
          .array()
          .of(yup.number())
          .min(1, t("required", { ns: "Shared" })),
      })
    ),
    defaultValues: {
      ocr_attachments: [],
    },
  });

  const { data: { validations } = {}, isLoading } = useRequestDependencies(
    "base_application_form",
    request.id
  );

  const { trigger } = useSWRMutation(
    [
      baseApiService.ENDPOINTS.request.shared.request,
      request.number,
      viewRequestAs.role,
    ],
    requestFetcher
  );

  const onSubmit = (data) => {
    setIsUpdatingRequest(true);
    requestService
      .updateRequest(request.number, {
        user_role: viewRequestAs.role,
        ...data,
      })
      .then((updatedRequest) => {
        trigger(updatedRequest, {
          optimisticData: (current) => ({ ...current, ...updatedRequest }),
        });
        handleClose();
      })
      .finally(() => setIsUpdatingRequest(false));
  };

  const handleModalClose = () => {
    handleClose();
    setFilesWithErrors([]);
  };

  return (
    <Modal show={show} backdrop="static">
      <BootstrapForm onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header className="d-flex flex-column align-items-start">
          <span className="mb-2">
            <Badge bg="primary">New</Badge>
            <Badge bg="warning" className="ms-1">
              BETA
            </Badge>
          </span>
          <Modal.Title>{t("upload_modal_header")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.FilesInput
            withOCR
            label={t("attachments_label")}
            name="ocr_attachments"
            disabled={isLoading || isUploading}
            control={control}
            error={errors.ocr_attachments}
            maxSize={
              validations?.ocr_attachments?.max_allowed_file_size_in_bytes
            }
            max={
              validations?.ocr_attachments?.attachments_count_rules.data?.max
            }
            min={
              validations?.ocr_attachments?.attachments_count_rules.data?.min
            }
            acceptedExtensions={
              validations?.ocr_attachments?.attachments_allowed_file_types.data
            }
            setExternalUploadingState={setIsUploading}
            setFilesWithErrors={setFilesWithErrors}
            setValue={setValue}
            text={
              <Trans i18nKey="attachments_requirements" ns="OCR">
                Chosen files with Doc Buddy must be:
                <ul>
                  <li>one page only;</li>
                  <li>one receipt/invoice only;</li>
                  <li>vertical; facing the top up;</li>
                  <li>clear and readable (no folds etc.)</li>
                </ul>
              </Trans>
            }
          />

          {filesWithErrors.length > 0 && (
            <FilesWithErrors files={filesWithErrors} />
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="outline-primary" onClick={handleModalClose}>
            {t("buttons.cancel", { ns: "Shared" })}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isUploading || isUpdatingRequest}
            isLoading={isUpdatingRequest}
          >
            {t("buttons.load", { ns: "Shared" })}
          </Button>
        </Modal.Footer>
      </BootstrapForm>
    </Modal>
  );
}

export default UploadToOCRModal;
